import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { devicesApi, LOW_REFILL_THRESHOLD } from '../api'
import useFetch from '../hooks/useFetch'
import {
  Page, PageHead, Card, StatGrid, Stat, TableWrapper, Table, StatusPill,
  Loading, ErrorState, EmptyState, Input, Select, Button,
} from '../Components/ui'
import { colors, bp } from '../theme'

const Bar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  h3 { margin: 0; color: ${colors.ink}; font-size: 1.15rem; }
`

const RegisterForm = styled.form`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  align-items: center;

  input { min-width: 200px; }

  @media (max-width: ${bp.mobile}) {
    input, select, button { width: 100%; }
  }
`

const FormError = styled.div`
  color: ${colors.red};
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
`

// One-time reveal of the API key returned by /device/create-device. The backend
// stores only a hash, so this is the only chance to copy it.
const KeyReveal = styled.div`
  border: 1px solid ${colors.green};
  background: ${colors.greenLight};
  border-radius: 10px;
  padding: 1rem 1.15rem;
  margin-bottom: 1.25rem;

  h4 { margin: 0 0 0.6rem; color: ${colors.greenDark}; font-size: 1rem; }
  .row { margin: 0.35rem 0; font-size: 0.9rem; color: ${colors.ink}; }
  .label { color: ${colors.slate}; margin-right: 0.4rem; }
  code {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    background: ${colors.card};
    border: 1px solid ${colors.line};
    border-radius: 6px;
    padding: 0.3rem 0.5rem;
    word-break: break-all;
    user-select: all;
  }
  .warn { color: ${colors.red}; font-size: 0.82rem; margin-top: 0.7rem; font-weight: 600; }
  .hint { color: ${colors.slate}; font-size: 0.82rem; margin-top: 0.4rem; }
  .actions { display: flex; gap: 0.5rem; margin-top: 0.85rem; flex-wrap: wrap; }
`

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—')

const Devices = () => {
  const { data: devices, loading, error, reload } = useFetch(() => devicesApi.list(), [])
  const [query, setQuery] = useState('')
  const [name, setName] = useState('')
  const [serial, setSerial] = useState('')
  const [location, setLocation] = useState('')
  const [status, setStatus] = useState('active')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [created, setCreated] = useState(null) // { name, serial, apiKey } — shown once
  const [copied, setCopied] = useState(false)

  const list = useMemo(() => devices || [], [devices])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return list.filter((d) => !q || d.name.toLowerCase().includes(q))
  }, [list, query])

  const online = list.filter((d) => d.statusState === 'success').length

  const register = async (e) => {
    e.preventDefault()
    if (!name.trim()) return setFormError('Device name is required.')
    // Serial is required: the dispenser authenticates by it (x-device-serial),
    // so a device without one can never claim a payment.
    if (!serial.trim()) return setFormError('Serial is required — the dispenser authenticates by it.')
    setSaving(true)
    setFormError('')
    setCopied(false)
    try {
      const device = await devicesApi.create({
        name: name.trim(),
        serial: serial.trim(),
        location: location.trim() || undefined,
        status,
      })
      setCreated(device) // reveal the API key once
      setName('')
      setSerial('')
      setLocation('')
      setStatus('active')
      reload()
    } catch (err) {
      setFormError(err.message || 'Could not register device.')
    } finally {
      setSaving(false)
    }
  }

  // Copy the key. navigator.clipboard is unavailable over plain http (e.g. the
  // dashboard on a LAN IP), so fall back to a hidden textarea + execCommand.
  const copyKey = async () => {
    const key = created?.apiKey || ''
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key)
      } else {
        const ta = document.createElement('textarea')
        ta.value = key
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false) // the key is select-all, so manual copy still works
    }
  }

  return (
    <Page>
      <PageHead>
        <h1>Dispensers</h1>
        <p>Register and monitor NBIoT EcoSmart Dispensers and their connectivity.</p>
      </PageHead>

      {loading && <Card><Loading label="Loading dispensers…" /></Card>}
      {error && <Card><ErrorState message={error.message} onRetry={reload} /></Card>}

      {!loading && !error && (
        <>
          <StatGrid cols={3}>
            <Stat label="Total Dispensers" value={list.length} sub="registered" />
            <Stat label="Online" value={online} sub="active & connected" />
            <Stat label="Offline" value={list.length - online} sub="need attention" />
          </StatGrid>

          <Card>
            <Bar><h3>Register a dispenser</h3></Bar>
            {formError && <FormError>{formError}</FormError>}

            {created && (
              <KeyReveal>
                <h4>✓ Dispenser registered — copy the API key now</h4>
                <div className="row"><span className="label">Name:</span>{created.name}</div>
                <div className="row"><span className="label">Serial:</span><code>{created.serial}</code></div>
                <div className="row"><span className="label">API key:</span><code>{created.apiKey}</code></div>
                <div className="warn">
                  Shown once and stored only hashed — it cannot be retrieved again.
                </div>
                <div className="hint">
                  Load it onto the device over Serial: <code>serial {created.serial}</code> then <code>devkey &lt;key&gt;</code>.
                </div>
                <div className="actions">
                  <Button type="button" onClick={copyKey}>{copied ? 'Copied ✓' : 'Copy key'}</Button>
                  <Button type="button" onClick={() => setCreated(null)} style={{ background: colors.slate }}>
                    Done
                  </Button>
                </div>
              </KeyReveal>
            )}

            <RegisterForm onSubmit={register}>
              <Input placeholder="Device name (e.g. Dispenser 1)" value={name}
                onChange={(e) => setName(e.target.value)} />
              <Input placeholder="Serial (e.g. DSP-001)" value={serial}
                onChange={(e) => setSerial(e.target.value)} />
              <Input placeholder="Location (optional)" value={location}
                onChange={(e) => setLocation(e.target.value)} />
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Online</option>
                <option value="inactive">Offline</option>
              </Select>
              <Button type="submit" disabled={saving}>
                {saving ? 'Registering…' : 'Register'}
              </Button>
            </RegisterForm>

            <Bar>
              <h3>Connected dispensers</h3>
              <Input placeholder="Search by name…" value={query}
                onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 220 }} />
            </Bar>

            {filtered.length === 0 ? (
              <EmptyState
                title={list.length === 0 ? 'No dispensers yet' : 'No matches'}
                message={list.length === 0
                  ? 'Register your first dispenser above to start monitoring it.'
                  : 'Try a different search.'}
              />
            ) : (
              <TableWrapper>
                <Table min="760px">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Serial</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Signal</th>
                      <th>Refill</th>
                      <th>Last Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d) => (
                      <tr key={d.id}>
                        <td>{d.name}</td>
                        <td>{d.serial || '—'}</td>
                        <td><StatusPill state={d.statusState} label={d.statusLabel} /></td>
                        <td>{d.location || '—'}</td>
                        <td>{d.signal || '—'}</td>
                        <td>
                          {d.refillLevel == null ? '—' : (
                            <span style={{
                              color: d.refillLevel <= LOW_REFILL_THRESHOLD ? colors.red : colors.slate,
                              fontWeight: d.refillLevel <= LOW_REFILL_THRESHOLD ? 700 : 400,
                            }}>
                              {d.refillLevel}%{d.refillLevel <= LOW_REFILL_THRESHOLD ? ' · low' : ''}
                            </span>
                          )}
                        </td>
                        <td>{fmtDate(d.lastActive)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            )}
            <p style={{ color: colors.slate, fontSize: '0.82rem', marginTop: '1rem' }}>
              Location, signal, and refill telemetry arrive with the extended device schema
              (Phase 3/4) — shown as “—” until the dispenser reports them.
            </p>
          </Card>
        </>
      )}
    </Page>
  )
}

export default Devices
